/* ==========================================================================
   Size unit switch (in | cm)

   The buttons hold both units and CSS on the root class shows one, so this
   file never re-renders them. It only:

     1. handles clicks on the in | cm control,
     2. persists the choice to localStorage,
     3. keeps the selected-value label next to the option name in the same
        unit as the buttons.

   (3) is the awkward part. assets/variant-label-fix.js writes the raw option
   value -- pipes and all -- into .variant__label--selected on variant change,
   on label clicks, and on a repeating interval for several seconds after
   load. So every one of those elements gets a MutationObserver, caches the
   raw value in data-unit-raw the first time a pipe appears, and re-renders
   only the half matching the current unit.

   The first paint is server-rendered as two spans, which CSS resolves -- no
   flash of the wrong unit while this deferred file is still loading.
   ========================================================================== */

(function () {
  'use strict';

  var STORAGE_KEY = 'iwa:size-unit';
  var SELECTED_SELECTOR = '.variant__label--selected[data-unit-selected-label]';
  var SPAN_SELECTOR = '.iwa-unit, .iwa-unit__extra';

  var root = document.documentElement;
  var watched = new WeakSet();
  var writing = false;

  function currentUnit() {
    if (root.classList.contains('unit-in')) return 'in';
    if (root.classList.contains('unit-cm')) return 'cm';
    return null;
  }

  /* Identify a part by its text, never by its position: this catalogue has
     both imperial-first and metric-first values, and an extra label can sit
     at either end. Mirrors the detection in snippets/variant-button.liquid. */
  function classify(text) {
    if (/cm/i.test(text)) return 'cm';
    if (text.indexOf('"') !== -1) return 'in';
    return 'extra';
  }

  function splitValue(raw) {
    var parts = [];
    var hasCm = false;
    var hasIn = false;

    raw.split('|').forEach(function (piece) {
      var text = piece.trim();
      if (!text) return;
      var kind = classify(text);
      if (kind === 'cm') hasCm = true;
      if (kind === 'in') hasIn = true;
      parts.push({ kind: kind, text: text });
    });

    return { parts: parts, dual: hasCm && hasIn };
  }

  /* Extra labels stay in both units; the other unit's part drops out. */
  function render(value, unit) {
    return value.parts
      .filter(function (part) {
        return part.kind === 'extra' || part.kind === unit;
      })
      .map(function (part) {
        return part.text;
      })
      .join(' ');
  }

  /* Bosluk farklarina bagisik karsilastirma: Liquid'in bastigi span'ler arasinda
     satir sonu var, JS'in bastiklarinda yok. Sadece esitlik icin kullanildigi
     icin butun boslugu atmak guvenli. */
  function squash(text) {
    return String(text).replace(/\s+/g, '');
  }

  function renderSelectedLabel(el) {
    var trimmed = el.textContent.trim();

    /* A pipe means the raw option value has just been written in. */
    if (trimmed.indexOf('|') !== -1) {
      el.setAttribute('data-unit-raw', trimmed);
    }

    var raw = el.getAttribute('data-unit-raw');
    if (!raw) return;

    var value = splitValue(raw);
    if (!value.dual) {
      el.removeAttribute('data-unit-raw');
      return;
    }

    var allText = value.parts
      .map(function (part) {
        return part.text;
      })
      .join('');
    var current = squash(trimmed);

    /* Span'ler yerinde ve icerik guncel: hangi birimin gorunecegi CSS'in isi,
       burada yapilacak bir sey yok. */
    if (el.querySelector(SPAN_SELECTOR) && current === squash(allText)) return;

    /* Ne ham deger ne de bizim render'imiz: tema baska bir varyantin degerini
       yazmis (ornegin olcu tasimayan 'Set of 3'). Dokunma, onbellegi bosalt. */
    if (
      current !== squash(raw) &&
      current !== squash(allText) &&
      current !== squash(render(value, 'in')) &&
      current !== squash(render(value, 'cm'))
    ) {
      el.removeAttribute('data-unit-raw');
      return;
    }

    /* Butun parcalari span olarak bas: secili etiket de butonlarla ayni sekilde
       CSS ile doner, birim degistiginde yeniden render gerekmez, ve serbest
       etiket ayrica stillenebilir. innerHTML yok -- her parca textContent ile
       yazildigi icin kacis derdi de yok. */
    writing = true;
    el.textContent = '';
    value.parts.forEach(function (part) {
      var span = document.createElement('span');
      span.className =
        part.kind === 'extra' ? 'iwa-unit__extra' : 'iwa-unit iwa-unit--' + part.kind;
      span.textContent = part.text;
      el.appendChild(span);
    });
    writing = false;
  }

  function watchSelectedLabel(el) {
    if (watched.has(el)) return;
    watched.add(el);

    new MutationObserver(function () {
      if (writing) return;
      renderSelectedLabel(el);
    }).observe(el, { childList: true, characterData: true, subtree: true });

    renderSelectedLabel(el);
  }

  function eachSelectedLabel(fn) {
    var labels = document.querySelectorAll(SELECTED_SELECTOR);
    for (var i = 0; i < labels.length; i++) fn(labels[i]);
  }

  function syncControls(unit) {
    var buttons = document.querySelectorAll('[data-unit-switch-btn]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute(
        'aria-pressed',
        buttons[i].getAttribute('data-unit') === unit ? 'true' : 'false'
      );
    }
  }

  function scan() {
    eachSelectedLabel(watchSelectedLabel);
    syncControls(currentUnit());
  }

  function setUnit(unit) {
    if (unit !== 'in' && unit !== 'cm') return;

    root.classList.remove('unit-in', 'unit-cm');
    root.classList.add('unit-' + unit);

    try {
      window.localStorage.setItem(STORAGE_KEY, unit);
    } catch (e) {
      /* Private mode or a full quota -- the switch still works for this page. */
    }

    syncControls(unit);
    eachSelectedLabel(renderSelectedLabel);
  }

  /* Capture phase: the control sits inside the option's <label>, so without
     both of these the click would activate the label and, through
     variant-label-fix.js, change the selected variant. */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var button = target.closest('[data-unit-switch-btn]');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();
      setUnit(button.getAttribute('data-unit'));
    },
    true
  );

  function start() {
    scan();

    /* The same hooks variant-label-fix.js listens on, for AJAX-swapped
       product content. */
    document.addEventListener('variant:change', scan);
    document.addEventListener('page:loaded', scan);

    /* Catches pickers inserted by quick view and section rendering. Cheap:
       scan() is one querySelectorAll and a no-op for anything already
       watched. */
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        scan();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
